import "./DragAndDropFile.scss";

import React, { useCallback, useRef, useState } from "react";
import clsx from "clsx";

import Text from "../../common/Text/Text";
import * as strings from "../../../strings";
import { TextElement } from "../../../lib/util/LocaleUtils";
import { Translatable } from "../../../lib/util/Translatable";

export interface DragAndDropFileProps {
	className?: string;
	onAfterDrop?: (files: FileList) => void;
	placeholder?: TextElement;
	icon?: React.ReactNode;
	accept: string[];
	errorMessage?: string | Translatable;
	mobilePlaceholder?: TextElement;
	showError?: boolean;
}

const DragAndDropFile = (props: DragAndDropFileProps) => {
	const [active, setActive] = useState(false);
	const [disableError, setDisableError] = useState(false);

	const fileInput = useRef<HTMLInputElement | null>(null);

	const {
		className,
		placeholder,
		icon,
		accept,
		errorMessage,
		mobilePlaceholder,
		showError,
		onAfterDrop,
	} = props;

	const handleDragOver = useCallback(
		(e: React.DragEvent) => {
			e.stopPropagation();
			e.preventDefault();
			e.dataTransfer.dropEffect = "copy";
			if (!active) {
				setActive(true);
				setDisableError(true);
			}
			return false;
		},
		[active],
	);

	const handleDragEnd = useCallback(
		(e: React.DragEvent) => {
			e.stopPropagation();
			e.preventDefault();
			if (active) {
				setActive(false);
			}
			return false;
		},
		[active],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();

			if (active) {
				setActive(false);
				setDisableError(false);
			}

			if (typeof onAfterDrop === "function") {
				onAfterDrop(e.dataTransfer.files);
			}
		},
		[active, onAfterDrop],
	);

	const handleClick = useCallback(() => {
		if (!fileInput.current) {
			return;
		}

		fileInput.current.click();
	}, []);

	const handleFilesChange = useCallback(() => {
		if (typeof onAfterDrop === "function") {
			onAfterDrop(fileInput.current?.files ?? new FileList());
		}
	}, [onAfterDrop]);

	return (
		<div className={"DragAndDropFile-wrapper"}>
			<div
				role={"none"}
				className={clsx("DragAndDropFile", className, {
					"--active": active,
					"--error": showError,
				})}
				draggable={true}
				onClick={handleClick}
				onDragOver={handleDragOver}
				onDragLeave={handleDragEnd}
				onDragEnd={handleDragEnd}
				onDrop={handleDrop}
			>
				<svg
					viewBox={"0 0 300 102"}
					preserveAspectRatio={"none"}
					className={"DragAndDropFile__border"}
				>
					<rect
						x={"0"}
						y={"1"}
						width={"300"}
						height={"100"}
						rx={"7"}
						ry={"7"}
					/>
				</svg>
				{icon}
				<Text
					tag={"p"}
					className={clsx("DragAndDropFile__copy", {
						"--mobileHide": mobilePlaceholder,
					})}
				>
					{active
						? strings.CONTROLS_DRAG_DROP_DROP_FILE
						: placeholder || strings.CONTROLS_UPLOAD_TITLE}
				</Text>

				{mobilePlaceholder && (
					<Text
						tag={"p"}
						className={"DragAndDropFile__copy DragAndDropFile__copy--mobile"}
					>
						{active
							? strings.CONTROLS_DRAG_DROP_DROP_FILE
							: mobilePlaceholder || strings.CONTROLS_UPLOAD_TITLE}
					</Text>
				)}
				<input
					type={"file"}
					name={"file"}
					style={{ display: "none" }}
					ref={fileInput}
					onChange={handleFilesChange}
					accept={accept.join(",")}
				/>
			</div>
			<Text
				tag={"p"}
				safeHtml={true}
				className={clsx(
					"DragAndDropFile__copy",
					"DragAndDropFile__copy--error",
					{
						"--active": showError && !disableError,
					},
				)}
			>
				{errorMessage}
			</Text>
		</div>
	);
};

export default DragAndDropFile;
